import usePayoutStructures from '@/hooks/usePayoutStructures';
import { useNavigate } from 'react-router-dom';
import routes from '@/routes/routes-enums';
import { toast } from 'react-toastify';
import TournamentPayoutStructureForm from './components/TournamentPayoutStructureForm';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '@/redux/root';
import { resetState } from '@/redux/slices/payoutStructure';
import { useEffect } from 'react';
import { PayoutBracketValidationError, validatePayoutBrackets } from '@repo/common-types';

const CreatePayoutStructureForm = () => {
  const { createPayoutStructureMutation, createOrUpdateRangeBracketsMutation, validateBracketsMutation } =
    usePayoutStructures();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { name, playersColumns, rankRows } = useSelector((state: AppState) => state.payoutStructure);

  // Reset state when component mounts to ensure clean slate for new payout structure
  useEffect(() => {
    dispatch(resetState());
  }, [dispatch]);

  const onSubmit = async () => {
    try {
      if (!playersColumns || !Array.isArray(playersColumns)) {
        toast.error('Invalid form data: playersColumns is missing or invalid');
        return;
      }

      const formattedBrackets = playersColumns.reduce((acc, curr) => {
        curr.percentages.forEach((percentage, index) => {
          const rankFrom = rankRows[index]?.rankFrom;
          const rankTo = rankRows[index]?.rankTo;

          if (Number(percentage) === 0) {
            return;
          }

          acc.push({
            percentage: Number(percentage),
            playersFrom: curr.playersFrom,
            playersTo: curr.playersTo,
            rankFrom,
            rankTo,
          });
        });

        return acc;
      }, []);

      validatePayoutBrackets(formattedBrackets);

      await validateBracketsMutation(formattedBrackets);

      const createdStructure = await createPayoutStructureMutation({
        name: name,
      });

      if (!createdStructure) {
        toast.error('Failed to create payout structure');
        return;
      }

      await createOrUpdateRangeBracketsMutation({
        payoutStructureId: createdStructure.id,
        brackets: formattedBrackets,
      });

      toast.success('Payout structure created successfully');
      navigate(routes.TOURNAMENT_PAYOUT_STRUCTURES);
    } catch (error: any) {
      if (error instanceof PayoutBracketValidationError) {
        toast.error(error.message);
        return;
      }
      toast.error(error?.response?.data?.message || 'Failed to create payout structure');
    }
  };

  return <TournamentPayoutStructureForm title="Create New Payout Structure" onSubmit={onSubmit} />;
};

export default CreatePayoutStructureForm;
